import {
  Children,
  cloneElement,
  useEffect,
  useRef,
  useMemo,
  MutableRefObject,
} from "react"

import { SemanticState, InputSize, LoaderSize } from "../../enumerations"
import { PopOver } from "../datepicker/components/popover"
import { Expander } from "../expander"
import { FloatingLabel } from "../floating-label"
import { InputButton } from "../input-button"
import { InputCustomIcon, InputCustomIconLeft } from "../input-custom-icon"
import { InputIcon, InputIconLeft } from "../input-icon"
import { InputMessage } from "../input-message"
import { Loader } from "../loader"
import { SelectAction } from "../select/select-action"
import { SelectList } from "../select/select-list"
import { SelectOptionTitle } from "../select/select-option-title"
import { SelectOption } from "../select/select-options"
import { SelectOptionAll } from "../select/select-options/select-option-all"

import {
  InputBaseContainer,
  InputBaseContentContainer,
  InputBaseControlsContainer,
  LoaderContainer,
  InputPrefix,
} from "./styles"
import { GenericInputProps, InputBaseContainerProps } from "./types"

export const GenericInput = ({ children, ...props }: GenericInputProps) => {
  return cloneElement(Children.only(children), props)
}

type InputChildren = {
  inner: any[]
  right: any[]
  outer: any[]
  messages: any[]
  messageIds: any[]
}

export const InputBase = ({
  className,
  id,
  isIdNotUsedDirectly,
  isErrorStateOverriden,
  ariaOwns,
  ariaBusy,
  ariaDescribedBy,
  ariaInvalid,
  ariaRequired,
  ariaExpanded,
  ariaMultiselectable,
  ariaLabelledBy,
  children,
  onRef,
  role,
  tabIndex,
  isLoading,
  isFocused,
  isPrefixVisible,
  isDisabled,
  svgIcon,
  customIcon,
  prefix,
  transform,
  size = InputSize.Large,
  type,
  onFocus,
  onBlur,
  onKeyDown,
  onMouseDown,
  semanticState = SemanticState.Default,
  onClick,
}: InputBaseContainerProps) => {
  const refInputBase: MutableRefObject<HTMLDivElement | null> = useRef(null)
  const messagesId = useMemo(() => `${id}-messages`, [id])

  useEffect(() => {
    onRef && refInputBase && onRef(refInputBase)
  }, [onRef, refInputBase.current])

  // Identifying which of the child nodes are controls and which are related to simple content
  const sortedChildren = Children.toArray(children)
    .sort((a: any, b: any) => {
      // Sorting the children to accumulate the ids of messages to pass them to the input fields
      switch (a.type) {
        case InputMessage:
          return b.type !== InputMessage ? -1 : 1
        default:
          return b.type === InputMessage ? 1 : -1
      }
    })
    .reduce(
      (accumulator: InputChildren, child: any, index) => {
        // Injecting props into each child based on the component type
        if (!child) {
          return accumulator
        }
        const injectableProps = {
          size,
          semanticState,
          isFocused,
          isDisabled,
          key: index,
          ...child.props,
        }
        switch (child.type) {
          case InputMessage:
            return {
              ...accumulator,
              messages: [...accumulator.messages, cloneElement(child, injectableProps)],
            }
          case InputIcon:
            return {
              ...accumulator,
              right: [...accumulator.right, cloneElement(child, injectableProps)],
            }
          case InputButton:
          case InputCustomIcon:
            return {
              ...accumulator,
              right: [...accumulator.right, cloneElement(child, injectableProps)],
            }
          case GenericInput:
            return {
              ...accumulator,
              inner: [
                ...accumulator.inner,
                cloneElement(child, {
                  ...injectableProps,
                  ...(accumulator.messageIds.length
                    ? { "aria-describedby": `[${messagesId}]` }
                    : {}),
                }),
              ],
            }
          case SelectOption:
          case SelectOptionTitle:
          case SelectOptionAll:
          case SelectAction:
            return accumulator
          case SelectList:
          case PopOver:
            return {
              ...accumulator,
              outer: [...accumulator.outer, cloneElement(child, injectableProps)],
            }
          default:
          case FloatingLabel:
            return {
              ...accumulator,
              inner: [cloneElement(child, injectableProps), ...accumulator.inner],
            }
        }
      },
      {
        inner: [],
        right: [],
        outer: [],
        messages: [],
        messageIds: [],
      },
    )

  let left = null
  if (svgIcon) {
    left = (
      <InputIconLeft
        svgIcon={svgIcon}
        transform={transform}
        isFocused={isFocused}
        isDisabled={isDisabled}
        semanticState={semanticState}
      />
    )
  } else if (customIcon) {
    left = (
      <InputCustomIconLeft
        isFocused={isFocused}
        isDisabled={isDisabled}
        semanticState={semanticState}
      >
        {customIcon}
      </InputCustomIconLeft>
    )
  }

  const getId = useMemo(() => {
    return isIdNotUsedDirectly ? {} : { id }
  }, [id])

  return (
    <>
      <InputBaseContainer
        className={className}
        // Accessibility

        {...getId}
        ref={refInputBase}
        role={role}
        type={type}
        tabIndex={tabIndex}
        aria-disabled={isDisabled}
        aria-invalid={ariaInvalid ?? semanticState === SemanticState.Error}
        aria-busy={ariaBusy}
        aria-owns={ariaOwns}
        aria-required={ariaRequired}
        aria-multiselectable={ariaMultiselectable}
        aria-expanded={ariaExpanded}
        aria-describedby={ariaDescribedBy ?? messagesId}
        aria-labelledby={ariaLabelledBy}
        // State props

        isLoading={isLoading}
        isFocused={isFocused}
        isDisabled={isDisabled}
        isErrorStateOverriden={isErrorStateOverriden}
        size={size}
        semanticState={semanticState}
        onBlur={onBlur}
        onFocus={onFocus}
        onKeyDown={onKeyDown}
        onClick={onClick}
        onMouseDown={onMouseDown}
      >
        <InputBaseContentContainer>
          {left}
          <InputPrefix isVisible={isPrefixVisible}>{prefix}</InputPrefix>
          {sortedChildren.inner}
        </InputBaseContentContainer>
        {(sortedChildren.right.length > 0 || isLoading) && (
          <InputBaseControlsContainer>
            {isLoading && (
              <LoaderContainer>
                <Loader size={LoaderSize.Small} />
              </LoaderContainer>
            )}
            {sortedChildren.right}
          </InputBaseControlsContainer>
        )}
      </InputBaseContainer>
      {sortedChildren.outer}
      <Expander id={messagesId}>{sortedChildren.messages}</Expander>
    </>
  )
}
